import StorageInterface from "./StorageInterface";
import StorageItemWithExpiration from "./StorageItemWithExpiration";
import ReactiveObject from "../ReactiveObject";
import Delay from "~/ts/library/Delay";
import Dictionary from "~/ts/library/Dictionary";
import ConsoleWrapper from "~/ts/library/Console";
import ObjectHelper from "~/ts/library/ObjectHelper";
import {computed, WritableComputedRef} from "vue";


let storage = ReactiveObject.make({});
//console.log("reactive storage", storage);
let eventListeners: ((key: string, e: StorageEvent) => void)[] = [];

abstract class ReactiveStorage implements StorageInterface {
    protected prefix: string = "";
    private wasErrorOnSet: boolean = false;

    abstract getType(): string;

    getDebugInfo(): Dictionary<any> {
        return {
            wasErrorOnSet: this.wasErrorOnSet
        }
    }

    getPrefix(): string {
        return this.prefix;
    }

    public setPrefix(prefix: string) {
        if (prefix.length) {
            prefix += "/";
        }
        this.prefix = prefix;
        for (let key of storage.keys()) {
            this.get(key, true);
        }
        return this;
    }

    protected get isForceReloadAvailable(): boolean {
        return true;
    }


    public static addEventListener(callback: (key: string, e: StorageEvent) => void) {
        eventListeners.push(callback);
    }

    protected static fireEvent(key: string, e: StorageEvent): void {
        for (let i = 0; i < eventListeners.length; i++) {
            let listener = eventListeners[i];
            if (listener) {
                listener(key, e);
            }
        }
    }

    public get(key: string, forceReload: boolean = true): any | null {
        let reactiveStorageValue = storage.get(key);
        let hasInReactiveStorage = ReactiveStorage.hasInReactiveStorage(key);
        if ((forceReload && this.isForceReloadAvailable) || !hasInReactiveStorage) {
            let realStorageValue = this.getFromStorage(key);
            if (JSON.stringify(realStorageValue) !== JSON.stringify(reactiveStorageValue) || !hasInReactiveStorage) {
                ReactiveStorage.setInReactiveStorage(key, realStorageValue);
                return this.get(key, false);
            }
        }
        if (typeof reactiveStorageValue == "object") {
            reactiveStorageValue = ObjectHelper.jsonClone(reactiveStorageValue);
        }
        return reactiveStorageValue;
    }

    private getStorageItemWithExpiration(key: string): StorageItemWithExpiration | undefined {
        let result: StorageItemWithExpiration | undefined;
        let expirationItem = this.get(key) as (null | StorageItemWithExpiration);
        if (expirationItem != null) {
            if (expirationItem.hasOwnProperty("value") && expirationItem.hasOwnProperty("ttl")) {
                expirationItem = new StorageItemWithExpiration(expirationItem.value, expirationItem.ttl, false);
                if (expirationItem.isAlive()) {
                    this.setexRemove(key, expirationItem.getCurrentTtl());
                    result = expirationItem;
                } else {
                    this.remove(key);
                }
            } else {
                result = new StorageItemWithExpiration(expirationItem, 0);
            }
        }
        return result;
    }

    public getCurrentTtl(key: string): number | null {
        let ttl = this.getStorageItemWithExpiration(key)?.getCurrentTtl();
        return ttl ? ttl : null;
    }

    public getex(key: string): any | null {
        return this.getStorageItemWithExpiration(key)?.getValue();
        /*
        let result: any = null;
        let expirationItem = this.get(key) as (null | StorageItemWithExpiration);
        if (expirationItem != null) {
            if (expirationItem.hasOwnProperty("value") && expirationItem.hasOwnProperty("ttl")) {
                expirationItem = new StorageItemWithExpiration(expirationItem.value, expirationItem.ttl, false);
                if (expirationItem.isAlive()) {
                    this.setexRemove(key, expirationItem.getCurrentTtl());
                    result = expirationItem.getValue();
                } else {
                    this.remove(key);
                }
            } else {
                result = expirationItem;
            }
        }
        return result;
        */
    }

    public remove(key: string): void {
        //PUSH_TO_DEBUG_CONSOLE(`REMOVE ${key}`);
        /*
        if (key.includes("Message")) {
            EventManagerInstance.emit("DEBUG", `UNSET ${key}`);
        }*/
        this.removeFromStorage(key);
        ReactiveStorage.removeFromReactiveStorage(key);
    }

    private isErrorLogged = false;

    public set(key: string, value: any, throwError?: boolean): void {

        /*
        if (key.includes("Message")) {
            EventManagerInstance.emit("DEBUG", `SET ${key} = ${JSON.stringify(value)}`);
        }*/
        ReactiveStorage.setInReactiveStorage(key, value);
        try {
            this.setToStorage(key, value, true);
        } catch (e) {
            this.wasErrorOnSet = true;
            if (!this.isErrorLogged) {
                this.isErrorLogged = true;
                ConsoleWrapper.error("Ошибка вставки в хранилище: ", e);
                if (throwError) {
                    throw e;
                }
            }
        }
    }

    public isErrorOnSet(): boolean {
        return this.wasErrorOnSet;
    }

    public setex(key: string, value: any, ttl: number, throwError?: boolean): void {
        this.set(key, new StorageItemWithExpiration(value, ttl), throwError);
        this.setexRemove(key, ttl);
    }

    private async setexRemove(key: string, ttl: number) {
        try {
            await Delay.make(ttl, "setexRemove_" + key, true);
            this.getex(key);
        } catch (e) {

        }
    }

    protected static hasInReactiveStorage(key: string): boolean {
        return storage.has(key);
    }

    protected static setInReactiveStorage(key: string, value: any): void {
        storage.set(key, value);
    }

    protected static removeFromReactiveStorage(key: string) {
        storage.set(key, null);
        storage.remove(key);
    }

    public createValueWrapper<T>(key: string, defaultValue?: T): WritableComputedRef<T> {
        const instance = this;
        return computed({
            get(): T {
                return instance.get(key) ?? defaultValue;
            },
            set(value: T) {
                instance.set(key, value);
            }
        });
    }

    protected abstract getFromStorage(key: string): any | null;

    protected abstract setToStorage(key: string, value: any, throwError: boolean): void;

    protected abstract removeFromStorage(key: string): void;

    public abstract isAvailable(): boolean;

    public abstract isPersistent(): boolean;
}


export default ReactiveStorage;